import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Download,
  KeyRound,
  RefreshCw,
  CheckCircle2,
} from "lucide-react";
import {
  CoverageShell,
  ErrorMessage,
  CopyButton,
  CodeBlock,
} from "./CoverageShared";
import { coverageFetch, fragmentValue, download } from "./coverageApi";
import {
  browserExample,
  htmlExample,
  serverExample,
  integrationGuide,
  SAMPLE_ORIGIN,
  sandboxEnvironmentExample,
  sandboxStartCommand,
} from "./integrationExamples";

const splitOrigins = (value) =>
  value
    .split("\n")
    .map((origin) => origin.trim())
    .filter(Boolean);
const steps = [
  ["demo", "See the demo", "Anyone evaluating RxRooster"],
  ["test", "Connect a test site", "Your IT team"],
  ["live", "Go live", "Your IT team + RxRooster"],
];

export default function CoverageSetup({ slug }) {
  const [token] = useState(() => fragmentValue("key"));
  const [partner, setPartner] = useState(null);
  const [testOrigins, setTestOrigins] = useState(SAMPLE_ORIGIN);
  const [liveOrigins, setLiveOrigins] = useState("");
  const [accent, setAccent] = useState("#167d8d");
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("demo");
  const base = `/partner-coverage/setup/${encodeURIComponent(slug)}`;

  const refresh = async () => {
    const p = await coverageFetch(base, { token });
    setPartner(p);
    setTestOrigins(p.sandboxOrigins.join("\n") || SAMPLE_ORIGIN);
    setLiveOrigins(p.requestedLiveOrigins.join("\n"));
    setAccent(p.accent);
    return p;
  };
  useEffect(() => {
    if (!token) {
      setError(
        "Open the full private setup link from your invitation. Ask RxRooster for a new link if you no longer have it.",
      );
      return;
    }
    refresh().catch((e) => setError(e.message));
  }, [slug, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (work) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await work();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const moveTo = (step) => {
    setTab(step);
    setNotice("");
    setError("");
  };
  const save = async (body) => {
    const p = await coverageFetch(base, { token, method: "PUT", body });
    setPartner(p);
    return p;
  };
  const createKey = () =>
    act(async () => {
      const data = await coverageFetch(`${base}/sandbox-key`, {
        token,
        method: "POST",
      });
      setKey(data.key);
      setPartner(data.partner);
      setNotice("Key created. Copy it into the .env file described below.");
    });
  const testAddressSaved = !!partner?.sandboxOrigins.includes(SAMPLE_ORIGIN);

  return (
    <CoverageShell
      label="PRIVATE SETUP GUIDE · FOR THE PARTNER’S IT TEAM"
      title={
        partner
          ? `${partner.name} + RxRooster`
          : "Connect your software to RxRooster"
      }
      description="Preview the experience, connect a test site, then request live access. The demo is ready to view; your IT team handles the connection."
      aside={
        <section className="cvg-note">
          <span className="cvg-eyebrow">WHO DOES WHAT</span>
          <h2>One clear handoff.</h2>
          <dl className="cvg-responsibilities">
            <dt>RxRooster</dt>
            <dd>
              Sends the invitation and approves live access after testing.
            </dd>
            <dt>Your IT team</dt>
            <dd>
              Keeps the API key on your server and adds the search panel to your
              software.
            </dd>
            <dt>Each customer</dt>
            <dd>
              Confirms its details or signs in to take its opening and shortlist
              into RxRooster.
            </dd>
          </dl>
          <p className="cvg-muted">
            Keep this setup link with your IT team. The separate demo link can
            be shared with anyone evaluating the experience.
          </p>
        </section>
      }
    >
      <ErrorMessage>{error}</ErrorMessage>
      {notice && (
        <p className="cvg-success" role="status">
          {notice}
        </p>
      )}
      {!partner && !error && <p role="status">Loading your setup guide…</p>}
      {partner && (
        <>
          <nav className="cvg-setup-steps" aria-label="Integration steps">
            {steps.map(([id, title, owner], index) => (
              <button
                key={id}
                type="button"
                aria-current={tab === id ? "step" : undefined}
                onClick={() => moveTo(id)}
              >
                <span className="cvg-step-number">{index + 1}</span>
                <span>
                  <b>{title}</b>
                  <small>{owner}</small>
                </span>
              </button>
            ))}
          </nav>

          {tab === "demo" && (
            <section className="cvg-panel">
              <span className="cvg-eyebrow">
                STEP 1 · NO INSTALLATION OR KEY NEEDED
              </span>
              <h2>See what your customers will use.</h2>
              <p>
                The demo shows a coverage gap inside a scheduling system. Select{" "}
                <b>Find coverage</b>, explore the provider deck, review matching
                sample services, and try <b>Express interest</b>.
              </p>
              <a
                className="cvg-button"
                href={partner.demoUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open your demo <ArrowUpRight size={16} />
              </a>
              <div className="cvg-role-callout">
                <h3>Just evaluating the partnership?</h3>
                <p>
                  You can stop here and share the demo. Send this private setup
                  link to your IT team when you are ready to connect your
                  software.
                </p>
              </div>
              <p className="cvg-muted">
                The demo uses fictional providers. In live use, Express interest
                feeds the company’s existing RxRooster Pipeline, where the
                normal listing and negotiation workflow continues.
              </p>
              <div className="cvg-step-footer">
                <button
                  className="cvg-button cvg-secondary"
                  onClick={() => moveTo("test")}
                >
                  Next: Connect a test site <ArrowRight size={16} />
                </button>
              </div>
            </section>
          )}

          {tab === "test" && (
            <>
              <section className="cvg-panel">
                <span className="cvg-eyebrow">STEP 2 · YOUR IT TEAM</span>
                <h2>Use the sandbox key on your server.</h2>
                <p>
                  A sandbox is a test connection with fictional providers. For
                  the example below, your computer runs a small server and a
                  sample webpage.
                </p>
                <ol
                  className="cvg-key-flow"
                  aria-label="How the sandbox key is used"
                >
                  <li>
                    <b>Your server</b>
                    <span>
                      Reads the sandbox key from <code>.env</code>.
                    </span>
                  </li>
                  <li>
                    <b>RxRooster</b>
                    <span>
                      Returns an authenticated iframe URL for that vacancy.
                    </span>
                  </li>
                  <li>
                    <b>Your webpage</b>
                    <span>
                      Loads that URL. RxRooster handles the rate field and
                      provider search.
                    </span>
                  </li>
                </ol>
                <p className="cvg-muted">
                  The key stays on the server. Do not put it in{" "}
                  <code>index.html</code> or the iframe URL.
                </p>
              </section>

              <section className="cvg-panel cvg-setup-task">
                <span className="cvg-eyebrow">A · ALLOW YOUR TEST WEBSITE</span>
                <h2>Start with the downloadable example.</h2>
                <p>
                  Its webpage runs at <code>{SAMPLE_ORIGIN}</code>. Save that
                  address so RxRooster allows it to open your search.
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    act(async () => {
                      await save({
                        sandboxOrigins: splitOrigins(testOrigins),
                        requestedLiveOrigins: partner.requestedLiveOrigins,
                        accent: partner.accent,
                      });
                      setNotice(
                        "Test addresses saved. Continue to your sandbox key below.",
                      );
                    });
                  }}
                >
                  <label>
                    Allowed test website addresses
                    <textarea
                      required
                      rows={2}
                      value={testOrigins}
                      onChange={(e) => setTestOrigins(e.target.value)}
                    />
                  </label>
                  <small>
                    Use the address above for the example. Your IT team can add
                    its own test websites, one address per line, without a page
                    path.
                  </small>
                  <div className="cvg-actions">
                    <button
                      className="cvg-button cvg-secondary"
                      disabled={busy}
                    >
                      Save test addresses
                    </button>
                    {testAddressSaved && (
                      <span className="cvg-inline-success">
                        <CheckCircle2 size={16} /> Example address saved
                      </span>
                    )}
                  </div>
                </form>
              </section>

              <section className="cvg-panel cvg-setup-task">
                <span className="cvg-eyebrow">B · GET YOUR SERVER KEY</span>
                <h2>Your sandbox key</h2>
                <p>
                  Copy this key into the <code>.env</code> file in the next
                  step. That is the only place you paste it in the example.
                </p>
                {!partner.sandboxKeyConfigured && (
                  <>
                    <button
                      className="cvg-button"
                      disabled={busy || !partner.sandboxOrigins.length}
                      onClick={createKey}
                    >
                      <KeyRound size={16} /> Create sandbox key
                    </button>
                    {!partner.sandboxOrigins.length && (
                      <p className="cvg-muted">
                        Save your test address above first.
                      </p>
                    )}
                  </>
                )}
                {key ? (
                  <div className="cvg-secret">
                    <b>Copy this key before leaving the page.</b>
                    <textarea
                      readOnly
                      value={key}
                      aria-label="Sandbox server key"
                    />
                    <CopyButton text={key}>Copy sandbox key</CopyButton>
                  </div>
                ) : (
                  partner.sandboxKeyConfigured && (
                    <p className="cvg-success">
                      A sandbox key has already been issued. Use the copy your
                      IT team saved in its server settings.
                    </p>
                  )
                )}
                {partner.sandboxKeyConfigured && (
                  <details className="cvg-reference">
                    <summary>Need a replacement key?</summary>
                    <p>
                      This immediately invalidates the old key and its open
                      sessions. Update your server and test again.
                    </p>
                    <button
                      disabled={busy}
                      className="cvg-button cvg-secondary"
                      onClick={createKey}
                    >
                      Replace sandbox key
                    </button>
                  </details>
                )}
              </section>

              <section className="cvg-panel cvg-setup-task">
                <span className="cvg-eyebrow">
                  C · PUT THE KEY IN YOUR TEST FOLDER
                </span>
                <h2>Three files. One place for the key.</h2>
                <p>
                  Download these files into one folder on your computer. Rename{" "}
                  <code>.env.example</code> to <code>.env</code>, then open it
                  in a text editor and replace the placeholder with your sandbox
                  key.
                </p>
                <div className="cvg-starter-files">
                  <div>
                    <b>server.mjs</b>
                    <p>Opens search sessions using the key.</p>
                    <button
                      className="cvg-button cvg-secondary"
                      onClick={() =>
                        download(
                          "server.mjs",
                          serverExample(partner),
                          "text/javascript",
                        )
                      }
                    >
                      <Download size={15} /> Server file
                    </button>
                  </div>
                  <div>
                    <b>index.html</b>
                    <p>Displays the iframe. No key goes here.</p>
                    <button
                      className="cvg-button cvg-secondary"
                      onClick={() =>
                        download(
                          "index.html",
                          htmlExample(partner),
                          "text/html",
                        )
                      }
                    >
                      <Download size={15} /> Webpage file
                    </button>
                  </div>
                  <div>
                    <b>.env.example → .env</b>
                    <p>Paste your sandbox key in this file.</p>
                    <button
                      className="cvg-button cvg-secondary"
                      onClick={() =>
                        download(".env.example", sandboxEnvironmentExample)
                      }
                    >
                      <Download size={15} /> Key settings file
                    </button>
                  </div>
                </div>
                <CodeBlock
                  label=".env · replace the placeholder with your key"
                  code="RXROOSTER_API_KEY=PASTE_YOUR_SANDBOX_KEY_HERE"
                />
                <p className="cvg-muted">
                  Keep <code>.env</code> out of source control. When you build
                  this into your own software, your hosting provider’s server
                  environment variables or secret manager serve the same
                  purpose.
                </p>
              </section>

              <section className="cvg-panel cvg-setup-task">
                <span className="cvg-eyebrow">
                  D · RUN IT AND CHECK THE CONNECTION
                </span>
                <h2>Open your first embedded search.</h2>
                <p>
                  With Node.js 20.6 or newer installed, open a terminal in the
                  folder containing the three files and run:
                </p>
                <CodeBlock
                  label="Run from your test folder"
                  code={sandboxStartCommand}
                />
                <p>
                  Then open{" "}
                  <a href={SAMPLE_ORIGIN} target="_blank" rel="noreferrer">
                    {SAMPLE_ORIGIN} <ArrowUpRight size={13} />
                  </a>{" "}
                  and click <b>Find coverage</b>. The supplied opening loads the
                  provider deck automatically. Save a provider and return here
                  to check the result.
                </p>
                <p className="cvg-muted">
                  Use this exact address. If another sample is already running
                  on port 8787, stop it first so you test the files and key you
                  just configured.
                </p>
                <div className="cvg-connection-result" role="status">
                  <b>
                    {partner.sandboxTestedAt
                      ? "Test connection confirmed"
                      : "Waiting for your first test search"}
                  </b>
                  <p>
                    {partner.sandboxTestedAt
                      ? `RxRooster received a search from your embedded sandbox on ${new Date(partner.sandboxTestedAt).toLocaleString()}.`
                      : "Opening the public demo does not complete this step. Run your downloaded example using your key."}
                  </p>
                </div>
                <button
                  disabled={busy}
                  className="cvg-button cvg-secondary"
                  onClick={() =>
                    act(async () => {
                      const p = await refresh();
                      setNotice(
                        p.sandboxTestedAt
                          ? "Your test connection works. You can request live access next."
                          : "No embedded test received yet. Run the sample with the key in .env, click Find coverage, then browse the provider deck inside RxRooster.",
                      );
                    })
                  }
                >
                  <RefreshCw size={16} /> Check my connection
                </button>
                <details className="cvg-reference">
                  <summary>If the example does not connect</summary>
                  <ul>
                    <li>
                      Keep all three files in the same folder and name the
                      settings file exactly <code>.env</code>.
                    </li>
                    <li>
                      Replace the placeholder with the complete sandbox key,
                      starting with <code>rxc_test_</code>.
                    </li>
                    <li>
                      Save <code>{SAMPLE_ORIGIN}</code> above and use that exact
                      address in your browser.
                    </li>
                    <li>
                      Keep the sample server running. If you replace its key,
                      update <code>.env</code> and restart the sample.
                    </li>
                  </ul>
                </details>
                <div className="cvg-step-footer">
                  <button
                    disabled={!partner.sandboxTestedAt || busy}
                    className="cvg-button"
                    onClick={() => moveTo("live")}
                  >
                    Next: Request live access <ArrowRight size={16} />
                  </button>
                </div>
              </section>
            </>
          )}

          {tab === "live" && (
            <>
              <section className="cvg-panel">
                <span className="cvg-eyebrow">
                  STEP 3 · YOUR IT TEAM + RXROOSTER
                </span>
                <h2>Move from sample providers to live services.</h2>
                <p>
                  Your test key is for the sandbox. RxRooster provides a
                  separate live key after reviewing your connection.
                </p>
                {!partner.sandboxTestedAt && (
                  <div className="cvg-role-callout">
                    <p>
                      Complete your test connection first. Then submit the
                      website where your customers will use the search.
                    </p>
                    <button
                      className="cvg-button cvg-secondary"
                      onClick={() => moveTo("test")}
                    >
                      Back to test connection
                    </button>
                  </div>
                )}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    act(async () => {
                      await save({
                        sandboxOrigins: partner.sandboxOrigins,
                        requestedLiveOrigins: splitOrigins(liveOrigins),
                        accent,
                      });
                      setNotice(
                        "Live website addresses submitted for RxRooster review. Your existing test result is preserved.",
                      );
                    });
                  }}
                >
                  <label>
                    Your live website addresses
                    <textarea
                      required
                      rows={2}
                      value={liveOrigins}
                      onChange={(e) => setLiveOrigins(e.target.value)}
                      placeholder="https://schedule.yourcompany.com"
                    />
                  </label>
                  <small>
                    HTTPS addresses only, one per line. These remain subject to
                    RxRooster approval.
                  </small>
                  <details className="cvg-reference">
                    <summary>Optional brand color</summary>
                    <label className="cvg-color">
                      Brand accent
                      <input
                        type="color"
                        value={accent}
                        onChange={(e) => setAccent(e.target.value)}
                      />
                    </label>
                  </details>
                  <button
                    className="cvg-button"
                    disabled={busy || !partner.sandboxTestedAt}
                  >
                    Submit for live review
                  </button>
                </form>
                <p>
                  <b>Status:</b>{" "}
                  {partner.status === "active"
                    ? "Live access approved"
                    : partner.status === "paused"
                      ? "Connection paused — contact RxRooster"
                      : partner.requestedLiveOrigins.length
                        ? "Live website addresses submitted; RxRooster approval required"
                        : "Sandbox only"}
                </p>
              </section>
              <section className="cvg-panel">
                <h2>What happens after approval?</h2>
                <ol className="cvg-steps">
                  <li>
                    <b>RxRooster provides the live key.</b>
                    <span>
                      Your IT team saves it in your deployed server’s settings
                      and implements the authenticated connection described in
                      the technical guide. The downloadable server is a sandbox
                      example.
                    </span>
                  </li>
                  <li>
                    <b>Each customer brings its opening and details.</b>
                    <span>
                      Your backend supplies the complete opening and company
                      user details. The customer confirms email and phone or
                      signs in on RxRooster.
                    </span>
                  </li>
                  <li>
                    <b>Your customers search and express interest.</b>
                    <span>
                      The requisition appears in Listings and saved interests
                      appear in Pipeline. After the normal reviews and matching
                      checks, conversations continue there.
                    </span>
                  </li>
                </ol>
              </section>
            </>
          )}

          <section className="cvg-panel">
            <details className="cvg-reference">
              <summary>Technical reference for your IT team</summary>
              <p>
                Use this when adapting the working example to your own system:
                complete opening and registration fields, authentication, iframe
                messages, errors and launch checks.
              </p>
              <button
                className="cvg-button cvg-secondary"
                onClick={() =>
                  download(
                    `${partner.slug}-integration.md`,
                    integrationGuide(partner),
                  )
                }
              >
                <Download size={16} /> Download technical guide
              </button>
              <CodeBlock label="Server example" code={serverExample(partner)} />
              <CodeBlock label="Browser example" code={browserExample} />
              <pre className="cvg-guide">{integrationGuide(partner)}</pre>
            </details>
          </section>
        </>
      )}
    </CoverageShell>
  );
}
